import { useEffect, useRef, useState } from 'react';
import { C } from '@ridgewill/brand';
import { renderSVG } from 'uqr';
import { authClient } from '../lib/auth-client.js';
import { Field, Note } from '../components/form.js';
import { Modal } from '../components/Modal.js';
import { CodeInput } from '@ridgewill/brand/react';
import { SecurityGate } from '../components/SecurityGate.js';
import { VerifyFirst } from '../components/EmailVerification.js';
import { useEmailVerified } from '../lib/email-verification.js';

/**
 * Two-factor enrolment, including backup codes.
 *
 * ── Backup codes are the point, not a bonus ──────────────────────────
 *
 * auth#10 calls this a NEW capability rather than a port: today a lost phone is
 * an unrecoverable lockout with no admin reset flow. Enrolling somebody in TOTP
 * without giving them a way back in would make that worse rather than better, so
 * the codes are part of enrolment and not a thing to find later.
 *
 * ── WHERE EACH STEP HAPPENS, AND WHY THE LINE IS THERE (account#64) ──
 *
 * THE DIALOG holds everything up to and including verification: the password,
 * the QR code, the manual key, the six-digit box. THE SECTION BEHIND IT holds
 * the backup codes, which appear as the dialog closes.
 *
 * This reverses the split account#58 shipped, where the dialog closed as the QR
 * appeared. Jon's call, and the argument that was made against it does not
 * vanish — it becomes the reason the line falls between verification and the
 * codes rather than at the end.
 *
 * The old argument: enrolment is a multi-step job with a secret to photograph
 * and codes to write down, and a modal cannot be referred back to, so dismissing
 * it mid-scan loses the codes. Every part of that is true of the CODES and none
 * of it is true of the rest. A secret abandoned mid-scan costs a fresh start; a
 * dismissed Escape over the backup codes costs the account its way back in
 * after a lost phone, and they are shown once.
 *
 * So the irrecoverable thing is the one thing not behind a control whose whole
 * job is to make things go away — while the flow that IS recoverable stays in
 * one place, which is what the dialog is good at.
 *
 * ── The QR code is generated IN THE BROWSER, and that matters ────────
 *
 * `uqr` renders the otpauth URI to inline SVG. Zero dependencies, no network.
 *
 * The alternative every tutorial reaches for is an `img` pointed at a chart API,
 * which sends the TOTP SECRET to a third party in a query string — the one value
 * whose entire purpose is that only two parties know it. `qrcode` was the other
 * candidate and pulls yargs and pngjs, a Node CLI toolchain, into a browser
 * bundle.
 *
 * Inline SVG also means no second request at all, which is the same reason this
 * app self-hosts its font.
 */
/** An enrolment in progress. Lives only as long as the dialog does. */
type Enrolment = { totpURI: string; backupCodes: string[] };

/** What the dialog is currently asking about. All three ask for the password
 *  first; only `setup` has a second step behind it. */
type Intent = 'setup' | 'regenerate' | 'disable';

/**
 * The dialog's words, per intent, in one place.
 *
 * `action` completes "Confirm your password to <action>" inside `SecurityGate`,
 * so it is lower case and starts with a verb. Kept as a table rather than three
 * ternaries at the call site because it is the whole difference between the
 * three dialogs — everything else about them is identical, which is the point.
 */
const DIALOG: Record<Intent, { title: string; action: string; busy: string }> = {
  setup: {
    title: 'Set up two-factor authentication',
    action: 'set up two-factor authentication',
    busy: 'Starting…',
  },
  regenerate: {
    title: 'New backup codes',
    action: 'get a new set of backup codes',
    busy: 'Making new codes…',
  },
  disable: {
    title: 'Turn off two-factor authentication',
    action: 'turn off two-factor authentication',
    busy: 'Turning it off…',
  },
};

export function TwoFactor({
  twoFactorEnabled,
  hasPassword,
  onChanged,
}: {
  twoFactorEnabled: boolean;
  /**
   * From the sign-in methods lookup the Security screen already does.
   *
   * `undefined` while outstanding, and the setup control waits for it rather
   * than guessing — see the idle branch, where guessing wrong in one direction
   * offers a doomed dialog and in the other hides the feature from somebody who
   * can use it.
   */
  hasPassword: boolean | undefined;
  onChanged: () => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  /**
   * Which of the three the dialog is asking about, and NOT cleared when it
   * closes — deliberately, along with `enrolment` below.
   *
   * `Modal` keeps rendering for the length of its exit animation, so anything
   * driving its contents has to survive the close or the dialog spends 140ms
   * fading out showing something else: the step before the one just finished,
   * or the wrong question entirely. Both are reset when the dialog next opens,
   * which is the only moment a stale one could be seen.
   */
  const [intent, setIntent] = useState<Intent>('setup');
  /** `null` until the password is accepted and enrolment has begun. */
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  /** The backup codes, on the page behind the dialog. Also where regenerating
   *  puts a fresh set. */
  const [codes, setCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Open the dialog on a fresh question. The only place the previous one is
   *  cleared — see `intent` for why it is not cleared on close. */
  function ask(next: Intent) {
    setError(null);
    setEnrolment(null);
    setCode('');
    setIntent(next);
    setDialogOpen(true);
  }

  /**
   * Enrolment starts with the PASSWORD, not with a QR code.
   *
   * `/two-factor/enable` requires it, and that is right: binding a second factor
   * from an already-open session would otherwise let anyone who reaches an
   * unlocked laptop attach their own authenticator — and keep access after the
   * password is changed.
   */
  async function begin(credential: { password?: string }) {
    setError(null);
    setBusy(true);
    /**
     * The password is sent again, and the gate having passed changes nothing
     * about that. `/two-factor/enable` compares it itself, which is where the
     * real check has always been — see `SecurityGate`, which says at length why
     * dropping it here would move the only check into a browser.
     *
     * `?? ''` covers the account with no password at all. It cannot enrol
     * anyway, which is why the idle branch does not offer it the dialog; if one
     * ever reaches here the server refuses, and that refusal is a sentence
     * rather than a crash.
     */
    const { data, error: err } = await authClient.twoFactor.enable({
      password: credential.password ?? '',
    });
    setBusy(false);
    if (err || !data) {
      setError(err?.message ?? 'That password was not accepted.');
      return;
    }
    /**
     * NARROW ON `method` BEFORE READING THE SECRET (better-auth 1.7.0).
     *
     * `enable` used to return `{ totpURI, backupCodes }` flat. It now returns a
     * discriminated union — `{ method: 'otp' }` or
     * `{ method: 'totp', totpURI, backupCodes }` — because 1.7 added OTP-only
     * enrolment, where there is no secret to show and no QR code to scan.
     *
     * This portal enrols TOTP only: the server has no `otpOptions.sendOTP`
     * configured, so `method: 'otp'` is unreachable today. It is handled rather
     * than cast away, because the day somebody turns OTP on at the service, the
     * alternative is `undefined` reaching a QR renderer — a blank dialog with no
     * error, which is the exact shape of failure RidgeWill/account#49 is about.
     */
    if (data.method !== 'totp') {
      setError('This account was enrolled by one-time code rather than an authenticator app. Reload to see its current state.');
      return;
    }
    // The dialog STAYS OPEN and its contents advance. See the header.
    setEnrolment({ totpURI: data.totpURI, backupCodes: data.backupCodes });
  }

  /**
   * Verification is NOT a formality.
   *
   * Until a code from the authenticator is accepted, nobody has proved the
   * secret reached it — a failed scan, a mistyped key, a clock badly out of
   * step. Finishing without this step produces an account locked out at its next
   * sign-in, by the security feature that was supposed to protect it.
   *
   * `entered` is passed in rather than read from state: the sixth digit and this
   * call happen in the same event, and `code` still holds five of them.
   */
  async function verify(entered: string) {
    if (!enrolment || busy) return;
    setError(null);
    setBusy(true);
    const { error: err } = await authClient.twoFactor.verifyTotp({ code: entered });
    setBusy(false);
    setCode('');
    if (err) {
      setError(err.message ?? 'That code was not accepted. Check your app and try again.');
      return;
    }
    onChanged();
    /**
     * The codes land on the page and the dialog leaves, in that order and in one
     * commit. `enrolment` is left alone so the dialog has something to render
     * while it fades.
     */
    setCodes(enrolment.backupCodes);
    setDialogOpen(false);
  }

  /**
   * Turning it off, and minting fresh codes, both take the PASSWORD again —
   * they are the two ways to weaken or rotate the second factor, and an
   * unattended session should be able to do neither.
   *
   * The password arrives from the gate rather than from a box on the page, and
   * it is still SENT: better-auth compares it itself on both endpoints. See
   * `SecurityGate` for why the gate having passed changes nothing about that.
   */
  async function act(kind: 'disable' | 'regenerate', credential: { password?: string }) {
    const password = credential.password ?? '';
    setError(null);
    setBusy(true);
    if (kind === 'disable') {
      const { error: err } = await authClient.twoFactor.disable({ password });
      setBusy(false);
      if (err) return setError(err.message ?? 'That password was not accepted.');
      onChanged();
      setCodes(null);
      setDialogOpen(false);
      return;
    }
    const { data, error: err } = await authClient.twoFactor.generateBackupCodes({ password });
    setBusy(false);
    if (err || !data) return setError(err?.message ?? 'That password was not accepted.');
    setDialogOpen(false);
    /**
     * Regenerating INVALIDATES the previous set. Shown on the same screen that
     * says so, because somebody who still has the old codes written down will
     * otherwise keep a list that silently stopped working.
     */
    setCodes(data.backupCodes);
  }

  /**
   * The codes outrank everything else this section can show.
   *
   * Checked before `twoFactorEnabled` on purpose: enrolment finishing flips that
   * flag, and the managed view is where somebody lands afterwards — not instead
   * of the one screen the codes ever appear on.
   */
  /** An error belongs in the dialog while one is open, and on the page when
   *  the last thing that failed was not behind one. */
  const pageError = dialogOpen ? null : error;
  /** `/two-factor/enable` is refused until the address is proven (auth#587).
   *  Only the OFFER is replaced: an account that already has it keeps managing
   *  it, which nothing refuses. */
  const verified = useEmailVerified();

  const body = codes ? (
    <BackupCodes codes={codes} />
  ) : twoFactorEnabled ? (
    <Managed error={pageError} onAsk={ask} />
  ) : !verified ? (
    <>
      <p style={{ lineHeight: 1.5 }}>Add a second step at sign-in using an authenticator app.</p>
      <VerifyFirst />
    </>
  ) : hasPassword === undefined ? (
    <p style={{ color: C.muted }}>Checking&hellip;</p>
  ) : hasPassword ? (
    <Offer error={pageError} onSetUp={() => ask('setup')} />
  ) : (
    <NoPassword />
  );

  return (
    <Section title="Two-factor authentication">
      {body}

      {/*
        THE DIALOG SITS OUTSIDE THE BRANCH, and that is not tidiness.

        Enrolment finishing swaps `body` from the Set up button to the backup
        codes in the same commit that closes the dialog. Rendered inside that
        branch, the Modal would be UNMOUNTED by the swap rather than closed by
        it — React removes it on the spot and there is nothing left to fade,
        which is the exact defect account#61 fixed at every other call site.

        `open` as a prop rather than a mount condition, and a closed Modal
        renders nothing, so keeping it here costs a branch that returns null.
      */}
      <Modal
        open={dialogOpen}
        title={enrolment ? 'Scan this, then confirm' : DIALOG[intent].title}
        onClose={() => setDialogOpen(false)}
      >
        {enrolment ? (
          <Enrol
            enrolment={enrolment}
            code={code}
            busy={busy}
            error={error}
            onCode={setCode}
            onComplete={(entered) => void verify(entered)}
            onCancel={() => setDialogOpen(false)}
          />
        ) : (
          <>
            {intent === 'regenerate' ? (
              /* Said BEFORE the password, not under the buttons afterwards.
                 This is the one of the three that quietly invalidates something
                 somebody has written down, and the moment to say so is while
                 they can still change their mind. */
              <div style={{ marginBottom: '.9rem' }}>
                <Note>
                  New codes replace the old ones. Any list you have written down stops working.
                </Note>
              </div>
            ) : null}
            <SecurityGate
              action={DIALOG[intent].action}
              /**
               * Always true here, and not a guess.
               *
               * `/two-factor/enable` requires a password, so an account with
               * two-factor on has one — and the service does not set
               * better-auth's `allowPasswordless`, which is the only thing that
               * would let any of the three run without it. The `setup` branch is
               * the one that has to check, and it does: `hasPassword` decides
               * between `Offer` and `NoPassword` before this dialog can open.
               */
              hasPassword
              onVerified={(credential) => {
                if (intent === 'setup') return void begin(credential);
                void act(intent, credential);
              }}
              onCancel={() => setDialogOpen(false)}
            />
            {busy ? <p style={{ color: C.muted, marginTop: '1rem' }}>{DIALOG[intent].busy}</p> : null}
            {error ? <Problem>{error}</Problem> : null}
          </>
        )}
      </Modal>
    </Section>
  );
}

/**
 * Two-factor is already on: rotate the codes, or turn it off.
 *
 * ── THE BUTTONS ARE JUST THERE, AND THE DIALOG ASKS (Jon, 2026-09-02) ─
 *
 * This section kept the arrangement account#58 removed everywhere else: a
 * password box on the page with both controls disabled behind it, so the screen
 * opened on a filled-in form and the refusal came at the end. It is the last
 * place on the Security page that asked inline.
 *
 * Pressing a button now opens the dialog and the dialog asks. Same ordering
 * argument as the rest — a borrowed session gets nowhere and a legitimate
 * person finds out in one keystroke — with one addition specific to these two:
 * they are the ways to WEAKEN the second factor, and a control that needs no
 * setup to press is also the control an accidental click reaches. The dialog is
 * the pause.
 */
function Managed({
  error,
  onAsk,
}: {
  error: string | null;
  onAsk: (intent: Intent) => void;
}) {
  return (
    <>
      <p style={{ lineHeight: 1.5 }}>
        Two-factor authentication is <strong>on</strong> for this account.
      </p>

      {error && <Problem>{error}</Problem>}

      <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap', marginTop: '.5rem' }}>
        <button type="button" onClick={() => onAsk('regenerate')} style={buttonStyle}>
          New backup codes
        </button>
        <button type="button" onClick={() => onAsk('disable')} style={buttonStyle}>
          Turn off
        </button>
      </div>
      {/*
        The warning about replacing the codes has moved INTO the dialog, where
        it is read at the moment it applies. Left here it described a button
        that now only opens a question, and it sat under both of them while
        being true of one.
      */}
    </>
  );
}

/**
 * ── SET UP OPENS THE GATE, IT DOES NOT ASK INLINE (Jon, 2026-09-01) ──
 *
 * The password box used to sit on the page, so an unattended session showed a
 * filled-in form and the refusal came at the end. The dialog asks first.
 */
function Offer({ error, onSetUp }: { error: string | null; onSetUp: () => void }) {
  return (
    <>
      <p style={{ lineHeight: 1.5 }}>Add a second step at sign-in using an authenticator app.</p>
      {error && <Problem>{error}</Problem>}
      <button type="button" style={{ ...buttonStyle, marginTop: '1.5rem' }} onClick={onSetUp}>
        Set up
      </button>
    </>
  );
}

/**
 * ── AN ACCOUNT WITH NO PASSWORD IS TOLD, NOT OFFERED ─────────────────
 *
 * `/two-factor/enable` takes a password and has no branch for an account
 * without one, so a provider-only account pressing Set up could only ever be
 * refused. That is the same shape as the bug the Password card had — a control
 * leading to a screen that can only fail — so the section says what to do
 * instead. `SecurityGate` would not save it either: there is nothing to verify,
 * and passing straight through would land on the same refusal.
 */
function NoPassword() {
  return (
    <>
      <p style={{ lineHeight: 1.5 }}>Add a second step at sign-in using an authenticator app.</p>
      <p style={{ marginTop: '1rem', lineHeight: 1.5, color: C.textMid }}>
        Setting this up needs a password on the account, and this one does not have one yet. Set a
        password first, in the section above.
      </p>
    </>
  );
}

/**
 * The scan-and-confirm step, inside the dialog.
 *
 * Split out rather than left inline because it is the half of this component
 * that renders somewhere else — everything above it draws into the page and
 * this draws into a panel 480px wide.
 */
function Enrol({
  enrolment,
  code,
  busy,
  error,
  onCode,
  onComplete,
  onCancel,
}: {
  enrolment: Enrolment;
  code: string;
  busy: boolean;
  error: string | null;
  onCode: (value: string) => void;
  onComplete: (entered: string) => void;
  onCancel: () => void;
}) {
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {/*
          `dangerouslySetInnerHTML` is the API uqr offers — it returns an SVG
          STRING. The input is a URI this app just received from our own API and
          never anything a person typed, so no untrusted markup reaches here.
        */}
        <div
          aria-label="QR code for your authenticator app"
          style={{ width: 200, height: 200, background: '#fff', padding: 8, borderRadius: 6 }}
          dangerouslySetInnerHTML={{ __html: renderSVG(enrolment.totpURI) }}
        />
      </div>

      <p style={{ marginTop: '1rem', color: C.textMid, fontSize: '.9rem', lineHeight: 1.5 }}>
        Cannot scan it? Enter this key by hand:
      </p>
      {/*
        The secret in text as well, because a QR code is useless on the device
        displaying it — enrolling a password manager on the same machine is an
        ordinary thing to do, not an edge case.
      */}
      <code style={secretStyle}>{secretFrom(enrolment.totpURI)}</code>

      {/*
        A form around a field with no submit button, which is not an oversight.

        The sixth digit submits on its own — that is the whole point of the
        segmented input, and a Confirm button that could only ever be pressed at
        one moment is the step the pattern exists to remove. The form is what
        makes ENTER retry the same code after a refusal, which is otherwise
        impossible without clearing all six and typing them again.
      */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (code.length === 6) onComplete(code);
        }}
        style={{ marginTop: '1.5rem' }}
      >
        {/* The label is this app's to render — the shared component stops at
            the row of boxes, so it is not wrong in the hosted login, which has
            a different field primitive. See @ridgewill/brand/react. */}
        <Field id="code" label="Six-digit code from your app">
          <CodeInput
            id="code"
            value={code}
            onChange={onCode}
            onComplete={onComplete}
            autoFocus
          />
        </Field>

        {/*
          Present from first paint rather than mounted with the message, so a
          screen reader announces the change rather than the arrival — and it is
          the ONLY feedback there is, because the sixth digit submits with no
          button to press and nothing else moves until an answer comes back.

          `aria-live="polite"` rather than `role="status"`, which is the same
          announcement. The page behind this dialog carries the screen's one
          `role="status"` — the copy-all confirmation — and the dialog outlives
          its own close by the length of the exit animation, so the two would
          briefly both be "the status region": ambiguous to anyone reasoning
          about where an announcement lands, and to a test asking for it by role.
        */}
        <p
          aria-live="polite"
          aria-atomic="true"
          style={{ margin: '.25rem 0 0', color: C.muted, fontSize: '.9rem' }}
        >
          {busy ? 'Checking…' : ''}
        </p>
        {error && <Problem>{error}</Problem>}
      </form>

      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginTop: '.5rem' }}>
        <button type="button" onClick={onCancel} style={buttonStyle}>
          Cancel
        </button>
      </div>
      {/*
        Said before it happens rather than after. Closing here costs a fresh
        start — a new secret, a new QR, the password again — and somebody who
        has just photographed a QR code has every reason to think the work is
        banked.
      */}
      <p style={{ marginTop: '.75rem', color: C.textMid, fontSize: '.85rem', lineHeight: 1.45 }}>
        Close this and nothing is saved. You would need to start again with a new code.
      </p>
    </>
  );
}

/**
 * Pull the shared secret out of the otpauth URI for manual entry.
 *
 * Parsed rather than regexed, and guarded: a malformed URI must not blank the
 * whole screen at the one moment somebody is trying to enrol.
 */
export function secretFrom(totpURI: string): string {
  try {
    return new URL(totpURI).searchParams.get('secret') ?? '';
  } catch {
    return '';
  }
}

/**
 * Shown ONCE, and said so plainly.
 *
 * These are the answer to a lost phone. The server stores them hashed, so this
 * render is the only time they exist in readable form — a screen somebody could
 * click past without noticing would recreate the lockout this feature exists to
 * prevent, one step later.
 *
 * ── AND FOCUS COMES HERE, BECAUSE THE DIALOG JUST LEFT ───────────────
 *
 * `Modal` returns focus to whatever opened it, and the Set up button that
 * opened this one has been replaced by these codes. Focus would land on the
 * document body: a keyboard user is dropped at the top of the page and a screen
 * reader says nothing, at the one moment there is something that cannot be
 * recovered on the screen. The heading takes it instead, so the next thing read
 * is what the codes are for.
 *
 * A live region would announce it without moving focus, and would read out
 * every code as a wall of characters. Focus is the quieter answer.
 */
function BackupCodes({ codes }: { codes: string[] }) {
  const heading = useRef<HTMLParagraphElement>(null);
  useEffect(() => heading.current?.focus(), []);

  return (
    <>
      <p ref={heading} tabIndex={-1} style={{ lineHeight: 1.5, outline: 'none' }}>
        <strong>Save these backup codes now.</strong> Each one signs you in once if you lose your
        phone. This is the only time they will be shown.
      </p>
      <ul style={codesStyle}>
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <CopyAll codes={codes} />
    </>
  );
}

/**
 * COPYING HAS TO SAY SOMETHING, AND FAILING HAS TO SAY MORE (#9).
 *
 * The button called `navigator.clipboard?.writeText()` and discarded what came
 * back. Nothing moved on screen, so the only way to learn whether it had worked
 * was to paste somewhere else and look.
 *
 * ── THE BUTTON IS THE FEEDBACK, not a note beside it ────────────────
 *
 * The first attempt put a sentence next to the button and left the button
 * itself inert. That is a worse answer to the same question: the thing you
 * pressed gives no sign it did anything, and the eye has to find a second
 * element to learn the outcome. The control you acted on is where the result
 * belongs, so the label becomes "Copied" and the fill turns green.
 *
 * Silence on FAILURE is the other half, and it is why this is not only a label
 * swap. `navigator.clipboard` is undefined outside a secure context, and
 * `writeText` rejects when the document is not focused or permission is
 * refused. The `?.` swallowed the first and the `void` swallowed the second, so
 * both looked identical to success: nothing at all.
 *
 * These codes are shown once and they are the answer to a lost phone. A copy
 * that quietly did nothing recreates the exact lockout this screen exists to
 * prevent, one step later, and the person finds out when they need them.
 */
function CopyAll({ codes }: { codes: string[] }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // The confirmation outlives the click, and this stage is replaced when
  // enrolment finishes, so the timer has to be cancelled on the way out.
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy() {
    clearTimeout(timer.current);
    try {
      // NOT `navigator.clipboard?.` - an absent clipboard API is a failure to
      // report, not a step to skip. Optional chaining is what hid it.
      await navigator.clipboard.writeText(codes.join('\n'));
      setState('copied');
      timer.current = setTimeout(() => setState('idle'), 4000);
    } catch {
      setState('failed');
    }
  }

  const copied = state === 'copied';

  return (
    <>
      <button
        type="button"
        className="rw-press"
        onClick={() => void copy()}
        style={copied ? copiedButtonStyle : buttonStyle}
      >
        {/*
          BOTH LABELS ARE ALWAYS IN THE DOM, one of them invisible.

          "Copied" is narrower than "Copy all", so swapping the text alone
          shrinks the button at the moment it is pressed - the control moves
          under the cursor, and in a column of buttons the whole block twitches.

          Stacked in one grid cell, the button is always as wide as the WIDER
          label, so the state change is a colour and a word and nothing else.
          `visibility: hidden` rather than `display: none` is what makes that
          work: it still takes up its space.

          `aria-hidden` on the inactive one is belt and braces. The accname spec
          already excludes a `visibility: hidden` node, so the name is correct
          without it - but the name is the difference between a button that says
          "Copy all" and one that says "CopiedCopy all", and that is not a thing
          to leave resting on how a given screen reader treats a CSS property.
        */}
        <span style={labelStackStyle}>
          <span style={copied ? labelStyle : labelHiddenStyle} aria-hidden={!copied}>
            Copied
          </span>
          <span style={copied ? labelHiddenStyle : labelStyle} aria-hidden={copied}>
            Copy all
          </span>
        </span>
      </button>
      {/*
        A label change is a reliable signal to the eye and an unreliable one to a
        screen reader, which may not re-read a button it is already sitting on.
        This region is present but empty from first paint - a live region has to
        exist BEFORE its content changes for the change to be announced - and it
        is positioned off-screen rather than hidden, because `display: none` is
        not announced at all.
      */}
      <span role="status" style={srOnlyStyle}>
        {copied ? `All ${codes.length} codes copied to your clipboard.` : ''}
      </span>
      {state === 'failed' && (
        <Problem>
          Your browser would not let us copy them. Select the codes above and copy them yourself.
        </Problem>
      )}
    </>
  );
}

/**
 * The heading moved OUT to the card that wraps this section (screens/Security).
 *
 * `title` is kept in the signature and deliberately unused rather than deleted
 * from every call site: this component renders four different stages and each
 * one names itself, which is the clearest record of which stage a branch is.
 * Removing them would make the branches harder to tell apart to save nothing.
 */
function Section({ title: _title, children }: { title: string; children: React.ReactNode }) {
  // No max width. The card is the column now; constraining again here left a
  // band of empty white inside the border, which reads as a layout fault rather
  // than a reading measure. Prose that needs a measure sets its own.
  return <div>{children}</div>;
}

function Problem({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" style={{ marginTop: '.75rem', color: '#b3261e', fontSize: '.9rem' }}>
      {children}
    </p>
  );
}

const buttonStyle: React.CSSProperties = {
  marginTop: '1rem',
  padding: '.6rem 1rem',
  fontSize: '1rem',
  background: 'transparent',
  color: C.brand,
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 4,
  cursor: 'pointer',
};

/**
 * The copied state of the Copy button.
 *
 * Brand `success` tokens rather than a hand-picked green, so it matches every
 * other positive state in the platform. The transition is safe to declare: the
 * reduced-motion block in styles.css disables it globally with `!important`.
 */
const copiedButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: C.successBg,
  color: C.successText,
  border: `1px solid ${C.successText}`,
  transition: 'background .15s ease-out, color .15s ease-out, border-color .15s ease-out',
};

/** One grid cell holding both labels, so the button cannot resize when the word
 *  changes. `placeItems` centres the shorter label in the wider box. */
const labelStackStyle: React.CSSProperties = {
  display: 'inline-grid',
  placeItems: 'center',
};

const labelStyle: React.CSSProperties = { gridArea: '1 / 1' };

const labelHiddenStyle: React.CSSProperties = { ...labelStyle, visibility: 'hidden' };

/** Off-screen but READ OUT. `display: none` and `visibility: hidden` are both
 *  skipped by screen readers, which would make the live region above pointless. */
const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * KEEPS `word-break: break-all`, while the addresses on this page moved to
 * `overflow-wrap: anywhere` (Jon, 2026-09-03). Not an oversight.
 *
 * The complaint there was an address cut mid-domain when it would have fitted
 * on the next line. This is a base32 secret somebody transcribes into an
 * authenticator by hand: it has no words to cut between, it is read character by
 * character, and a break anywhere in it is expected rather than jarring.
 * `break-all` also fills each line completely, which is the easier shape to
 * copy from.
 *
 * The rule, if you are here to make the two agree: `break-all` for an opaque
 * string somebody transcribes, `anywhere` for something read as one thing.
 */
const secretStyle: React.CSSProperties = {
  display: 'block',
  marginTop: '.5rem',
  padding: '.5rem .75rem',
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 4,
  fontSize: '.85rem',
  wordBreak: 'break-all',
};

const codesStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: '1rem',
  margin: '1rem 0 0',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(9rem, 1fr))',
  gap: '.5rem',
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 6,
  fontFamily: 'ui-monospace, monospace',
};
